import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar as CalIcon, ChevronLeft, ChevronRight, MapPin, Landmark } from "lucide-react";

/** Visual Compliance Calendar — month grid with color-coded pills.
 *  Blue = Federal, Gold = State. Auto-scoped to worksite states via the
 *  server response (`worksite_states`). */
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const WEEK_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function ComplianceCalendar() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthIdx, setMonthIdx] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null); // clicked-day date string
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/compliance/calendar?year=${year}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const eventsByDate = useMemo(() => {
    const map = {};
    (data?.events || []).forEach((ev) => {
      (map[ev.date] = map[ev.date] || []).push(ev);
    });
    return map;
  }, [data]);

  const eventsInMonth = useMemo(() => (data?.events || []).filter((ev) => {
    const [y, m] = ev.date.split("-");
    return Number(y) === year && Number(m) - 1 === monthIdx;
  }), [data, year, monthIdx]);

  // Build the 6×7 month grid (leading/trailing days from adjacent months in muted style)
  const firstOfMonth = new Date(year, monthIdx, 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const grid = useMemo(() => {
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [startWeekday, daysInMonth]);

  const dateKey = (d) => `${year}-${String(monthIdx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div className="p-10">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><CalIcon className="w-4 h-4" /> § COMPLIANCE CALENDAR · NEXUS-FIRST</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Tax Deadlines</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Federal + state filing deadlines auto-scoped to every state where you have an active worksite. Adding a worksite in a new state instantly unlocks its deadlines below.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cal-ical-export" onClick={() => {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            window.open(`${base}/api/compliance/calendar.ics?year=${year}`, "_blank");
          }} className="border-2 border-ink px-3 py-2 hover:bg-ink hover:text-white font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2" title="Sync with Google Calendar / Outlook">
            📅 Export to iCal
          </button>
          <button data-testid="cal-prev" onClick={() => { if (monthIdx === 0) { setYear(year - 1); setMonthIdx(11); } else setMonthIdx(monthIdx - 1); }} className="border-2 border-ink px-3 py-2 hover:bg-ink hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
          <div data-testid="cal-heading" className="font-heading font-black text-2xl tracking-tight w-52 text-center">{MONTHS[monthIdx]} {year}</div>
          <button data-testid="cal-next" onClick={() => { if (monthIdx === 11) { setYear(year + 1); setMonthIdx(0); } else setMonthIdx(monthIdx + 1); }} className="border-2 border-ink px-3 py-2 hover:bg-ink hover:text-white"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Nexus banner */}
      <div className="hard-card p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="nexus-banner">
        <MapPin className="w-4 h-4 text-warn" />
        <div className="font-mono text-xs">
          <b>Nexus:</b> {data === null ? (
            <span className="text-muted-foreground">loading worksite nexus…</span>
          ) : (data?.worksite_states || []).length === 0 ? (
            <span className="text-muted-foreground">no active worksites yet — showing federal deadlines only.</span>
          ) : (
            (data.worksite_states || []).map((s) => {
              const meta = data.state_tax_meta?.[s];
              const provisional = meta?.tax_table_source === "provisional";
              return (
                <span key={s} className="ml-1 inline-flex items-center gap-1" data-testid={`nexus-pill-${s}`}>
                  <span className="px-1.5 py-0.5 bg-warn text-ink font-black">{s}</span>
                  {meta && (
                    <span
                      title={`${meta.deadline_count} state deadline(s) · tax table: ${meta.tax_table_source}${meta.levies_income_tax ? "" : " · no state income tax"}`}
                      className={`px-1 py-0.5 text-[9px] uppercase tracking-widest border ${provisional ? "border-bad text-bad" : "border-ink text-muted-foreground"}`}
                      data-testid={`nexus-source-${s}`}
                    >
                      {meta.levies_income_tax ? meta.tax_table_source.replace("_", " ") : "no tax"}
                    </span>
                  )}
                </span>
              );
            })
          )}
        </div>
        <div className="ml-auto flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#1D4ED8]" /> Federal</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-warn" /> State</span>
        </div>
      </div>

      {/* Month Grid */}
      <div className="hard-card p-4 mb-8" data-testid="calendar-grid">
        <div className="grid grid-cols-7 mb-2">
          {WEEK_LABELS.map((w) => (
            <div key={w} className="overline text-center py-2">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {grid.map((d, i) => {
            if (!d) return <div key={i} className="h-24 bg-secondary/40 border border-ink/10" />;
            const key = dateKey(d);
            const evs = eventsByDate[key] || [];
            const isSelected = selected === key;
            const isToday = new Date().toISOString().slice(0, 10) === key;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setSelected(isSelected ? null : key)}
                data-testid={`cal-day-${key}`}
                className={`h-24 border-2 text-left p-1.5 flex flex-col ${isSelected ? "border-ink bg-warn/20" : isToday ? "border-ink" : "border-ink/20 hover:border-ink"}`}
              >
                <div className={`font-mono text-[11px] font-black ${isToday ? "text-warn" : ""}`}>{d}</div>
                <div className="mt-1 space-y-0.5 overflow-hidden">
                  {evs.slice(0, 2).map((ev, k) => (
                    <div key={k} className={`truncate font-mono text-[9px] px-1 py-0.5 ${ev.category === "federal" ? "bg-[#1D4ED8] text-white" : "bg-warn text-ink"}`} title={ev.title}>{ev.title}</div>
                  ))}
                  {evs.length > 2 && <div className="font-mono text-[9px] text-muted-foreground">+{evs.length - 2} more</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected-day drawer */}
      {selected && eventsByDate[selected] && (
        <div className="hard-card p-4 mb-8" data-testid="cal-selected-drawer">
          <div className="overline mb-3">§ {selected} · {eventsByDate[selected].length} deadline{eventsByDate[selected].length === 1 ? "" : "s"}</div>
          {eventsByDate[selected].map((ev, i) => (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-ink/10 last:border-b-0">
              <span className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest font-black ${ev.category === "federal" ? "bg-[#1D4ED8] text-white" : "bg-warn text-ink"}`}>{ev.category === "federal" ? <><Landmark className="w-2.5 h-2.5 inline mr-1" />FEDERAL</> : ev.state}</span>
              <div className="flex-1">
                <div className="font-heading font-black text-sm">{ev.title}</div>
                <div className="font-mono text-[11px] text-muted-foreground">{ev.note}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full month list */}
      <div className="hard-card p-4">
        <div className="overline mb-3">§ ALL {MONTHS[monthIdx]} DEADLINES · {eventsInMonth.length}</div>
        {busy && <div className="font-mono text-xs text-muted-foreground">Loading…</div>}
        {!busy && eventsInMonth.length === 0 && (
          <div className="font-mono text-xs text-muted-foreground italic">No filing deadlines this month.</div>
        )}
        <ul className="divide-y divide-ink/10">
          {eventsInMonth.map((ev, i) => (
            <li key={i} className="flex items-start gap-3 py-2" data-testid={`month-event-${i}`}>
              <div className="font-mono text-[11px] font-black w-16">{ev.date.slice(5)}</div>
              <span className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest font-black ${ev.category === "federal" ? "bg-[#1D4ED8] text-white" : "bg-warn text-ink"}`}>{ev.category === "federal" ? "FED" : ev.state}</span>
              <div className="flex-1">
                <div className="font-heading font-black text-sm">{ev.title}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{ev.note}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
